import { z, ZodType } from "zod"
import {
    AIRSPACE_NOTAM_TYPES,
    AIRSPACE_TFR_TYPES,
    AirspaceNotamType,
    AirspaceTfrType,
    CANADIAN_AIRSPACE_RESTRICTION_CATEGORIES,
    CanadianAirspaceRestrictionCategory,
    ContentSortDirection,
    ContentSortMode,
    ContentViewMode,
    USA_AIRSPACE_RESTRICTION_CATEGORIES,
    UsaAirspaceRestrictionCategory,
} from "@nova/constants"
import { MapStyleNames } from "features/map/control/mapBackground"
import { overlayKeys } from "features/map/control/types"
import { TypedEventEmitter } from "@nova/typed-event-emitter"
import { useSyncExternalStore } from "react"

function localStorageEntry<TSchema extends ZodType>(entry: {
    schema: TSchema
    defaultState: z.infer<TSchema>
}) {
    return entry
}

export const localStorageRegistry = {
    contentViewMode: localStorageEntry({
        defaultState: ContentViewMode.Grid,
        schema: z.enum(ContentViewMode),
    }),
    "workspace-content-sort-mode": localStorageEntry({
        defaultState: ContentSortMode.DateModified,
        schema: z.enum(ContentSortMode),
    }),
    "workspace-content-sort-dir": localStorageEntry({
        defaultState: ContentSortDirection.Descending,
        schema: z.enum(ContentSortDirection),
    }),
    sidePanelExpandedLayerIds: localStorageEntry({
        defaultState: [] as string[],
        schema: z.array(z.string()),
    }),
    sidePanelExpandedAssetCategories: localStorageEntry({
        defaultState: [] as string[],
        schema: z.array(z.string()),
    }),
    mapBackground: localStorageEntry({
        defaultState: MapStyleNames.Light,
        schema: z.enum(MapStyleNames),
    }),
    overviewMapBackground: localStorageEntry({
        defaultState: MapStyleNames.Dark,
        schema: z.enum(MapStyleNames),
    }),
    flightPreviewMapBackground: localStorageEntry({
        defaultState: MapStyleNames.Satellite,
        schema: z.enum(MapStyleNames),
    }),
    mapOverlays: localStorageEntry({
        defaultState: {},
        schema: z.partialRecord(z.enum(overlayKeys), z.boolean()),
    }),
    overviewMapOverlays: localStorageEntry({
        defaultState: {},
        schema: z.partialRecord(z.enum(overlayKeys), z.boolean()),
    }),
    hotspotScanAreaShowState: localStorageEntry({
        defaultState: {} as Record<string, boolean>,
        schema: z.record(z.string(), z.boolean()),
    }),
    orthoScanAreaShowState: localStorageEntry({
        defaultState: {} as Record<string, boolean>,
        schema: z.record(z.string(), z.boolean()),
    }),
    airspaceFilters: localStorageEntry({
        defaultState: {
            enabled: true,
            categoriesVisible: {
                restrictions: false,
                tfrs: true,
                notams: true,
            },
            restrictions: {
                usa: Object.fromEntries(
                    USA_AIRSPACE_RESTRICTION_CATEGORIES.map((category) => [category, true])
                ) as Record<UsaAirspaceRestrictionCategory, boolean>,
                canada: Object.fromEntries(
                    CANADIAN_AIRSPACE_RESTRICTION_CATEGORIES.map((category) => [category, true])
                ) as Record<CanadianAirspaceRestrictionCategory, boolean>,
            },
            tfrs: Object.fromEntries(AIRSPACE_TFR_TYPES.map((type) => [type, true])) as Record<
                AirspaceTfrType,
                boolean
            >,
            notams: Object.fromEntries(AIRSPACE_NOTAM_TYPES.map((type) => [type, true])) as Record<
                AirspaceNotamType,
                boolean
            >,
        },
        schema: z.object({
            enabled: z.boolean(),
            categoriesVisible: z.object({
                restrictions: z.boolean(),
                tfrs: z.boolean(),
                notams: z.boolean(),
            }),
            restrictions: z.object({
                usa: z.record(z.enum(USA_AIRSPACE_RESTRICTION_CATEGORIES), z.boolean()),
                canada: z.record(z.enum(CANADIAN_AIRSPACE_RESTRICTION_CATEGORIES), z.boolean()),
            }),
            tfrs: z.record(z.enum(AIRSPACE_TFR_TYPES), z.boolean()),
            notams: z.record(z.enum(AIRSPACE_NOTAM_TYPES), z.boolean()),
        }),
    }),
    "VCF-QLTY-PRFRNC": localStorageEntry({
        defaultState: "high" as const,
        schema: z.union([z.literal("low"), z.literal("medium"), z.literal("high")]),
    }),
} as const satisfies Record<string, { defaultState: unknown; schema: ZodType }>

export type AirspaceState = z.infer<(typeof localStorageRegistry)["airspaceFilters"]["schema"]>
export type USAAirspaceRestrictions = AirspaceState["restrictions"]["usa"]
export type CanAirspaceRestrictions = AirspaceState["restrictions"]["canada"]
export type AirspaceCategory = keyof AirspaceState["categoriesVisible"]

type LeafPaths<T, Prefix extends string = ""> = {
    [K in keyof T & string]: T[K] extends boolean
        ? `${Prefix}${K}`
        : LeafPaths<T[K], `${Prefix}${K}.`>
}[keyof T & string]
export type AirspaceOptionPath = LeafPaths<AirspaceState>

type LocalStorageKey = keyof typeof localStorageRegistry
const emitter = new TypedEventEmitter<LocalStorageKey>()

const snapshotCache = new Map<LocalStorageKey, { raw: string | null; value: unknown }>()

export function getLocalStorageValue<TKey extends LocalStorageKey>(key: TKey) {
    const entry = localStorageRegistry[key]
    type Value = z.infer<(typeof localStorageRegistry)[TKey]["schema"]>

    if (typeof localStorage === "undefined") return entry.defaultState as Value

    const rawString = localStorage.getItem(key)
    const cached = snapshotCache.get(key)
    if (cached && cached.raw === rawString) return cached.value as Value

    const value = ((): Value => {
        try {
            const raw = JSON.parse(rawString ?? "")
            if (!raw) return entry.defaultState as Value
            const parsed = entry.schema.safeParse(raw)
            if (parsed.success) return parsed.data as Value
            return entry.defaultState as Value
        } catch {
            return entry.defaultState as Value
        }
    })()

    snapshotCache.set(key, { raw: rawString, value })
    return value
}

export function useLocalStorageValue<TKey extends LocalStorageKey>(key: TKey) {
    return useSyncExternalStore(
        (onStoreChange) => {
            emitter.on(key, onStoreChange)
            return () => emitter.off(key, onStoreChange)
        },
        () => getLocalStorageValue(key),
        () => getDefaultLocalStorageValue(key)
    )
}

export function getDefaultLocalStorageValue<TKey extends LocalStorageKey>(key: TKey) {
    return localStorageRegistry[key].defaultState as z.infer<
        (typeof localStorageRegistry)[TKey]["schema"]
    >
}

export function setLocalStorageValue<TKey extends LocalStorageKey>(
    key: TKey,
    value: z.infer<(typeof localStorageRegistry)[TKey]["schema"]>
) {
    if (typeof localStorage === "undefined") return

    localStorage.setItem(key, JSON.stringify(value))
    emitter.emit(key)
}
